import { useCallback, useEffect, useMemo, useState } from "react"

import { BehaviorSubject, concat, of } from "rxjs"
import { catchError, distinctUntilChanged, finalize, map, switchMap } from "rxjs/operators"

import NotificationContactData from "./NotificationContactData"
import NotificationContactsGetParams from "./NotificationContactsGetParams"
import { GetNotificationContactsObservable } from "./NotificationContactsProcess"
import NotificationGridData from "./NotificationGridData"
import handleAPIError from "../../../utils/handleAPIError"
import ApiResult from "../../ApiResult"

const PAGE_SIZE = 10

function useNotificationContactsLoad(poolId: number) {
  const [gridData, setGridData] = useState<NotificationGridData>({ Records: [], Pages: 0, Total: 0 })
  const [loadingContacts, setLoadingContacts] = useState(false)

  const initialParams = useMemo(
    () => ({
      PoolId: poolId,
      PageNumber: 1,
      PageSize: PAGE_SIZE,
    }),
    [poolId]
  )

  const page$ = useMemo(() => new BehaviorSubject<NotificationContactsGetParams>(initialParams), [initialParams])

  useEffect(() => {
    const fetchNotificationContacts = (params: NotificationContactsGetParams) => {
      return concat(
        of({ loading: true }),
        GetNotificationContactsObservable(params).pipe(
          map((result) => ({ loading: false, result })),
          catchError((error) => of({ loading: false, result: undefined, error })),
          finalize(() => setLoadingContacts(false))
        )
      )
    }

    setLoadingContacts(true)
    const subscription = page$
      .pipe(distinctUntilChanged(), switchMap(fetchNotificationContacts))
      .subscribe(({ loading, result, error }: ApiResult<NotificationGridData>) => {
        if (!loading && result) {
          const parsedRecords = result.Records.map((record: NotificationContactData) => ({
            ...record,
            selected: false,
          }))

          setGridData({
            ...result,
            Records: parsedRecords || [],
            Pages: result.Pages ?? 0,
            Total: result.Total ?? 0,
          })
        }

        if (error) {
          handleAPIError(error)
        }
      })
    return () => subscription.unsubscribe()
  }, [page$])

  const reloadContacts = useCallback(() => {
    page$.next({ ...page$.value, PageNumber: 1 })
  }, [page$])

  const selectContacts = useCallback((contactIds: number[], checked: boolean) => {
    setGridData((prev) => ({
      ...prev,
      Records: prev.Records?.map((c) => (contactIds.includes(c.ContactID) ? { ...c, selected: checked } : c)),
    }))
  }, [])

  const selectedContacts = useMemo(() => {
    return gridData.Records.filter((c) => c.selected)
  }, [gridData])

  return { loadingContacts, gridData, selectedContacts, page$, selectContacts, reloadContacts }
}

export default useNotificationContactsLoad
